import React, { useState, useRef } from "react";
import { Mic, Send, Loader2, Volume2, StopCircle } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Card, SandboxTag } from "../components/common";
import { toast } from "sonner";

const EXAMPLES = [
  "What is the status of our credit scoring use case?",
  "Show open PDPL findings for Healthcare.",
  "Which use cases have critical red-team findings?",
  "Which use cases are blocked at the Security gate?",
  "What are our highest AI risks?",
  "Show pending approvals.",
];

export default function Voice() {
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const speak = (msg) => {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(msg);
    u.lang = lang === "ar" ? "ar-SA" : "en-US";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const ask = async (q) => {
    const query = q || text;
    if (!query.trim()) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/voice/query", { text: query, lang });
      setResult(data);
      speak(data.response);
    } catch (e) { toast.error("Voice query failed"); }
    finally { setLoading(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((tr) => tr.stop());
        setLoading(true);
        const fd = new FormData();
        fd.append("audio", blob, "audio.webm");
        fd.append("lang", lang);
        try {
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setResult(data); setText(data.transcription); speak(data.response);
        } catch (e) { toast.error("ASR failed — using sandbox fallback"); }
        finally { setLoading(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (e) { toast.error("Microphone not available — type your query instead"); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const Trace = ({ label, value }) => (
    <div className="flex gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
      <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-slate-700 break-words">{value}</span>
    </div>
  );

  return (
    <div>
      <PageHeader title={t("nav.voice")} subtitle="Voice-first multilingual assistant — ASR → intent → sandbox query → response → TTS" summary="Voice assistant. Ask about use cases, risks, red team findings, approvals and regulations. Responses are synthetic sandbox data.">
        <SandboxTag text="SANDBOX VOICE" />
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Ask the Assistant">
            <div className="flex gap-2">
              <input data-testid="voice-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder={lang === "ar" ? "اكتب سؤالك..." : "Type your question or use the microphone..."}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[var(--qatar)]" />
              <button data-testid="voice-mic" onClick={recording ? stopRec : startRec} className={`rounded-xl p-2.5 ${recording ? "bg-red-600 text-white animate-pulse" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {recording ? <StopCircle className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
              <button data-testid="voice-send" onClick={() => ask()} disabled={loading} className="qatar-gradient rounded-xl px-4 py-2.5 text-white disabled:opacity-60">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 flip-x" />}</button>
            </div>
            {recording && <p className="mt-2 text-sm text-red-600">🔴 {t("listening")}</p>}
          </Card>

          {result && (
            <Card title="Response" right={<button onClick={() => speak(result.response)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50"><Volume2 className="h-4 w-4" /> Replay</button>}>
              <p data-testid="voice-response" className="rounded-xl bg-[var(--qatar-subtle)] p-4 text-sm text-slate-800">{result.response}</p>
              <div className="mt-4 space-y-1.5">
                <Trace label="Language Detected" value={result.language_detected} />
                <Trace label="Transcription" value={result.transcription} />
                <Trace label="Intent" value={result.intent} />
                <Trace label="Retrieved Records" value={`${(result.retrieved_records || []).length} record(s)`} />
                <Trace label="Voice Status" value={result.voice_status} />
              </div>
              {(result.retrieved_records || []).length > 0 && (
                <div className="mt-3 space-y-1">
                  {result.retrieved_records.map((r, i) => <div key={i} className="rounded-lg border border-slate-100 px-3 py-1.5 text-xs text-slate-600">{r.name_en || r.category || r.regulation || r.usecase_name || r.id}</div>)}
                </div>
              )}
            </Card>
          )}
        </div>

        <Card title="Example Questions">
          <div className="space-y-2">
            {EXAMPLES.map((e, i) => <button key={i} data-testid={`voice-example-${i}`} onClick={() => { setText(e); ask(e); }} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-start text-sm text-slate-600 hover:border-[var(--qatar-border)] hover:bg-[var(--qatar-subtle)]">{e}</button>)}
          </div>
        </Card>
      </div>
    </div>
  );
}
